<template>
    <span class="nf-number text-nowrap">{{ formattedNumber }}</span>
</template>

<script>
import { numberCommaFormat } from "@js/helpers.ts";

export default {
    name: "NumberFormatComponent",
    props: {
        number: {
            type: [Number, String],
            required: true,
        },
        append_text: {
            type: [Number, String],
            default: '',
        },
        prepend_text: {
            type: [Number, String],
            default: '',
        },
        // дополнительные настройки анимации
        animate: {          // выключить/включить анимацию
            type: Boolean,
            default: true,
        },
        duration: {         // длительность анимации в мс
            type: Number,
            default: 250,
        },
        decimals: {         // сколько знаков после запятой рисовать
            type: Number,
            default: 2,
        },
        easing: {           // кривая анимации: easeOutCubic | linear
            type: String,
            default: 'easeOutCubic',
        },
    },

    data() {
        return {
            currentValue: 0,
            _rafId: null,
            _from: 0,
            _to: 0,
            _startTs: 0,
        };
    },

    computed: {
        formattedNumber() {
            // Если вход пустой — показываем 0 с преф/суффиксом
            if (this.number === null || this.number === undefined || this.number === '') {
                return this.prepend_text + '0' + this.append_text;
            }

            const value = this.decimals > 0
                ? parseFloat(this.currentValue.toFixed(this.decimals))
                : Math.round(this.currentValue);

            return this.prepend_text + numberCommaFormat(value) + this.append_text;
        },
    },

    watch: {
        number: {
            immediate: true,
            handler(newVal, oldVal) {
                const target = this._toNumber(newVal);

                // первый рендер — без анимации
                if (oldVal === undefined) {
                    this.currentValue = target;
                    this._from = target;
                    this._to = target;
                    return;
                }

                this._startAnimation(target);
            }
        }
    },

    beforeUnmount() {
        this._cancelAnimation();
    },

    methods: {
        _toNumber(v) {
            const n = typeof v === 'string' ? parseFloat(v.replace(/\s/g, '')) : Number(v);
            return Number.isFinite(n) ? n : 0;
        },

        _cancelAnimation() {
            if (this._rafId) {
                cancelAnimationFrame(this._rafId);
                this._rafId = null;
            }
        },

        _ease(t) {
            if (this.easing === 'linear') return t;
            // easeOutCubic по умолчанию
            return 1 - Math.pow(1 - t, 3);
        },

        _startAnimation(target) {
            this._cancelAnimation();

            const reduce = typeof window !== 'undefined'
                && window.matchMedia
                && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            const shouldAnimate = this.animate && !reduce && this.duration > 0 && target !== this.currentValue;

            this._from = this.currentValue;
            this._to = target;

            if (!shouldAnimate) {
                this.currentValue = target;
                return;
            }

            this._startTs = performance.now();

            const tick = (now) => {
                const elapsed = now - this._startTs;
                const t = Math.min(1, elapsed / this.duration);
                const k = this._ease(t);

                this.currentValue = this._from + (this._to - this._from) * k;

                if (t < 1) {
                    this._rafId = requestAnimationFrame(tick);
                } else {
                    this.currentValue = this._to; // финальный снэп, чтобы не «плавала» последняя цифра
                    this._rafId = null;
                }
            };

            this._rafId = requestAnimationFrame(tick);
        },
    },
};
</script>

<style scoped>
/* Чтоб цифры не прыгали по ширине — табличные цифры */
.nf-number {
    font-variant-numeric: tabular-nums;
    -webkit-font-smoothing: antialiased;
    will-change: contents;
}
</style>
